import { ContactModalData } from '@/components/common/ContactModal/util';
import { EventLabel, InteractionEvent, trackEvent, TrackingData } from '@/helpers/GTM/GTMHelper';
import { PropertyDetails } from '@/models/queries/getPropertyDetails';
import { OfferType, PropertyTypeNumber } from '@/models/valuationFunnel';
import {
  ButtonEventProps,
  FormInputsForTracking,
  LANDING_PAGE_BUTTON_ACTION,
  LANDING_PAGE_BUTTON_EVENT,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
  StepInfo,
  stepInputKeyMap,
  stepInputKeyMapContactModal,
  TRACKING_ACTION,
  TRACKING_CATEGORY,
  TRACKING_LABEL,
  VirtualPageViewProps,
} from '@/tracking/types';
import { PageSource } from '@/types';
import { MenuItem } from '@smg-real-estate/domus';
import { Ref } from 'vue';

export const trackingVirtualPageView = ({
  subType,
  locale,
  stepInputs,
  moreParams,
}: VirtualPageViewProps): void => {
  trackEvent('virtual_pageview', {
    h_page_type_110: 'propertyvaluation',
    h_page_subtype_112: subType,
    h_content_language_116: locale,
    ...(stepInputs !== undefined && { h_funnel_step_input_141: stepInputs }),
    ...moreParams,
  });
};

const transformDataToStepInputValue = (key: string, value: string | number | boolean): string => {
  const trackingLabels = ['asap', '6months', '2years', 'over2years'];
  switch (key) {
    case 'propertyType':
      return value === PropertyTypeNumber.Apartment ? 'apartment' : 'house';
    case 'offerType':
      return value === OfferType.Selling
        ? 'selling'
        : value === OfferType.Buying
          ? 'buying'
          : 'other';
    case 'whenToSell':
      return trackingLabels[value as number];
    case 'aptDetails':
      return value ? 'true' : 'false';
    default:
      if (typeof value === 'boolean') {
        return value ? 'yes' : 'no';
      }
      return value.toString();
  }
};

const processInputKey = (
  formData: FormInputsForTracking,
  key,
  stepIndex,
  stepInputKeyMap: Record<string, StepInfo>,
) => {
  if (stepIndex !== undefined && stepInputKeyMap[key].step >= stepIndex) {
    return null; // Skip this entry
  }

  if (key in formData && formData[key] !== undefined) {
    const mappedKey = stepInputKeyMap[key].name;
    const encodedValue = transformDataToStepInputValue(key, formData[key]);
    return `${mappedKey}=${encodedValue}`;
  }

  return null;
};

export const isPropertyDetailDataPrefilled = (
  propertyDetailsData: PropertyDetails[],
  selectedPropertyDetailIndex: number,
) => selectedPropertyDetailIndex in propertyDetailsData;

export const getStepInputs = (
  formData: FormInputsForTracking,
  source?: string,
  stepIndex?: number,
): string => {
  const inputKeyMapArr = Object.keys(stepInputKeyMap);
  const resultArr = inputKeyMapArr
    .map((key) => processInputKey(formData, key, stepIndex, stepInputKeyMap))
    .filter(Boolean);

  if (source === PageSource.MarketAtlas) {
    resultArr.unshift(`source=${source}`);
  }

  return resultArr.join('&');
};

export const getStepInputsLLPContactModal = (formData: ContactModalData): string => {
  const inputKeyMapArr = Object.keys(stepInputKeyMapContactModal);
  const resultArr = inputKeyMapArr
    .map((key) => {
      if (key in formData && formData[key]) {
        const mappedKey = stepInputKeyMapContactModal[key];
        return `${mappedKey}`;
      }
      return null;
    })
    .filter(Boolean);

  return resultArr.join('&');
};

export const trackingSellerLeadSubmit = (
  leadId: string,
  additionalParams?: Record<string, string | number>,
): void => {
  const eventParams = {
    h_event_category_006: 'submit_sellerlead',
    h_event_action_007: 'input_emailResult',
    h_lead_id_066: leadId,
    ...additionalParams,
  };

  trackEvent('submit_sellerlead', eventParams);
};

export const trackingGenericClickEvent = (
  params: ButtonEventProps,
  moreParams?: Record<string, string>,
): void =>
  trackEvent(params.event, {
    h_event_category_006: params.category,
    h_event_action_007: params.action,
    h_event_label_008: params.label,
    ...moreParams,
  });

export const trackingFeatureDisplayEvent = (action: string, label: string): void =>
  trackEvent(TRACKING_CATEGORY.FEATURE_DISPLAY, {
    h_event_category_006: TRACKING_CATEGORY.FEATURE_DISPLAY,
    h_event_action_007: action,
    h_event_label_008: label,
  });

export const trackingMapInteractionEvent = (
  action: string,
  label: string,
  params?: Record<string, string>,
): void => {
  const eventParams = {
    h_event_category_006: LOCAL_LANDING_PAGE_CATEGORY.MARKET_MAP_CLICK,
    h_event_action_007: action,
    h_event_label_008: label,
    ...params,
  };

  trackEvent(LOCAL_LANDING_PAGE_EVENT.MARKET_MAP_CLICK, eventParams);
};

export const trackingFavouritesEvent = (favouritesCount: number): void => {
  trackEvent('favourites_show', {
    interaction_containerName: 'header',
    interaction_containerDetail: 'button',
    interaction_goal: 'goToFavourites',
    interaction_element: favouritesCount,
  });
};

export function trackingLandingPageButtonEvent(
  action: LANDING_PAGE_BUTTON_ACTION,
  event?: LANDING_PAGE_BUTTON_EVENT,
) {
  const eventParams = {
    h_event_category_006: 'cta_click',
    h_event_action_007: action,
    h_event_label_008: event,
  };

  trackEvent('cta_click', eventParams);
}

export function trackingClickEvent(
  action: TRACKING_ACTION,
  label: TRACKING_LABEL,
  showCategory = true,
  params?: Record<string, string>,
) {
  trackEvent(TRACKING_CATEGORY.BUTTON_CLICK, {
    ...(showCategory && { h_event_category_006: TRACKING_CATEGORY.BUTTON_CLICK }),
    h_event_action_007: action,
    h_event_label_008: label,
    ...params,
  });
}

export function trackUserAuthentication(params?: Record<string, string>): void {
  trackEvent(TRACKING_CATEGORY.AUTHENTICATION, {
    h_event_category_006: TRACKING_CATEGORY.AUTHENTICATION,
    h_event_action_007: 'auth_login',
    h_event_label_008: 'successful',
    h_login_status_035: 'loggedin',
    ...params,
  });
}

export function useAppTracking(locale: Ref<string>) {
  const handleTrackFooterClick = (data: TrackingData) => {
    trackEvent(InteractionEvent.FOOTER_CLICK, {
      h_event_category_006: InteractionEvent.FOOTER_CLICK,
      h_event_action_007: data.eventAction,
      h_event_label_008: data.eventLabel,
      non_interaction: false,
    });
  };

  const handleTrackHeaderClick = (data: TrackingData) => {
    trackEvent(InteractionEvent.HEADER_CLICK, {
      h_event_category_006: InteractionEvent.HEADER_CLICK,
      h_event_action_007: data.eventAction,
      h_event_label_008: data.eventLabel,
      non_interaction: false,
      h_content_language_116: data.contentLanguage,
    });
  };

  const hasTrackingLabel = (
    menuItem: MenuItem,
  ): menuItem is MenuItem & { trackingLabel: string } => {
    return 'trackingLabel' in menuItem && typeof menuItem.trackingLabel === 'string';
  };

  const getTrackingLabel = (menuItem: MenuItem): string => {
    if (hasTrackingLabel(menuItem)) {
      return menuItem.trackingLabel;
    }
    return InteractionEvent.OPEN_MY_ACCOUNT;
  };

  const handleNavTracking = (menuItem: MenuItem, _event: Event, isMobile = false) => {
    const eventLabel: string = isMobile ? EventLabel.SIDEBAR : EventLabel.TAB_NAVIGATION;
    handleTrackHeaderClick({
      eventAction: getTrackingLabel(menuItem),
      eventLabel,
      contentLanguage: locale.value,
    });
  };

  const handleMainNavTracking = (menuItem: MenuItem, event: Event) => {
    handleNavTracking(menuItem, event, false);
  };

  const handleMobileMenuNavTracking = (menuItem: MenuItem, event: Event) => {
    handleNavTracking(menuItem, event, true);
  };

  return {
    handleTrackFooterClick,
    handleTrackHeaderClick,
    handleMainNavTracking,
    handleMobileMenuNavTracking,
  };
}

export const trackingGenericInteractionEvent = (action: string): void => {
  trackEvent(InteractionEvent.GENERIC_INTERACTION, {
    h_event_category_006: InteractionEvent.GENERIC_INTERACTION,
    h_event_action_007: action,
  });
};
